import { trim } from "ramda";
import { Maybe } from "ramda-fantasy";

import createQuantumApi from "@quantum/rest-api-client";
import { buildQueryString } from "@quantum/utils";

import {
  MODO_APP_SERIES,
  MAP_ORIGENS_REQUISICAO,
  parseVisualization,
  serializeVisualization,
} from "../model";

const parseJanelasSelecaoJanela = listaDatas => {
  return listaDatas.map(datas => ({
    dataInicial: datas.dataInicial,
    dataFinal: datas.dataFinal,
    numeroDaJanela: datas.numeroDaJanela,
  }));
};

const joinPath = (...segments) =>
  segments
    .map(String)
    .map(trim)
    .filter(Boolean)
    .join("/");

export const createExtendedQuantumAPI = (...args) => {
  const api = createQuantumApi(...args);
  const modoApp = args[0].modoApp;

  const exporOrigem = req =>
    req.set("X-Quantum-Origem", MAP_ORIGENS_REQUISICAO[modoApp]);

  const { get, post, put } = api.client.use(exporOrigem);
  const { get: baseGet } = api.baseClient.use(exporOrigem);

  return {
    ...api,

    carregarDominio: () =>
      api.unbatchedClient.get(
        joinPath("/configuracaorelatorio", modoApp, "carregarDominio")
      ),

    obterDetalhesUsuario: () =>
      api.unbatchedClient.get("/configuracaorelatorio/obterDetalhesUsuario"),

    obterDescricaoMedidaSelecionada: medidasSelecionadas => {
      return api.unbatchedClient
        .post(
          joinPath(
            "/configuracaorelatorio",
            modoApp,
            "obterDescricaoMedidaSelecionada"
          )
        )
        .send(medidasSelecionadas);
    },

    obterGruposCompartilhamento: () =>
      api.unbatchedClient.get(
        "/configuracaorelatorio/obterGruposCompartilhamentoUsuario"
      ),

    compartilharArquivo: ({
      arquivo,
      nomeArquivoCompartilhado,
      codigosUsuariosACompartilhar,
    }) =>
      api.unbatchedClient
        .post("/configuracaorelatorio/compartilharArquivo")
        .send({
          arquivo,
          nomeArquivoCompartilhado,
          codigosUsuariosACompartilhar,
        }),

    obterDatasUteisPeriodoJanela: (dataBase, selecaoJanela) =>
      api.unbatchedClient
        .get(
          `/periodos/obterDatasUteisPeriodoJanela?${buildQueryString({
            quantidadeDias: selecaoJanela.tamanhoJanela,
            quantidadeJanelas: selecaoJanela.quantidadeJanelas,
            ajusteDias: selecaoJanela.ajusteDias,
            dataBase: dataBase,
          })}`
        )
        .then(listaJanelas => parseJanelasSelecaoJanela(listaJanelas)),

    obterMesesPeriodoJanela: (dataBase, selecaoJanela) =>
      api.unbatchedClient
        .get(
          `/periodos/obterMesesPeriodoJanela?${buildQueryString({
            quantidadeMeses: selecaoJanela.tamanhoJanela,
            quantidadeJanelas: selecaoJanela.quantidadeJanelas,
            periodicidade: selecaoJanela.periodicidade,
            ajusteDias: selecaoJanela.ajusteDias,
            dataBase: dataBase,
          })}`
        )
        .then(listaJanelas => parseJanelasSelecaoJanela(listaJanelas)),

    obterMedidasUsuario: () => api.unbatchedClient.get("/medidas/usuario/"),

    gerarRelatorio: configuracaoRelatorio => {
      return api.unbatchedClient
        .post(joinPath("/configuracaorelatorio", modoApp, "gerarRelatorio"))
        .send(configuracaoRelatorio);
    },

    obterListaArquivosMedidas: () => {
      return api.unbatchedClient
        .get(
          joinPath(
            "/configuracaorelatorio",
            modoApp,
            "obterListaArquivosMedidas"
          )
        )
        .then(arquivos =>
          arquivos.map(arquivo => ({
            ...arquivo,
            dataModificacao:
              arquivo.dataModificacao != null
                ? new Date(arquivo.dataModificacao)
                : null,
          }))
        );
    },

    obterListaTemplatesMedidas: () => {
      return api.unbatchedClient.get(
        joinPath(
          "/configuracaorelatorio",
          modoApp,
          "obterListaTemplatesMedidas"
        )
      );
    },

    abrirArquivoMedidas: arquivo => {
      return api.unbatchedClient
        .post(joinPath("/configuracaorelatorio", modoApp, "abrirArquivo"))
        .send(arquivo);
    },

    salvarArquivoMedidas: (
      arquivo,
      medidasSelecionadas,
      configuracaoGlobalRelatorio,
      visualizacao,
      medidasParaPersonalizar
    ) => {
      return api.unbatchedClient
        .post("/configuracaorelatorio/salvarArquivo")
        .send({
          arquivo,
          configuracao: {
            configuracaoGlobalRelatorio: {
              ...configuracaoGlobalRelatorio,
              yamlConfiguracao: null,
              nomeGrupoAtivosAberto: null,
              nomeGrupoMedidasAberto: null,
            },
            medidasSelecionadas,
            configuracoesAdicionais: Maybe(visualizacao)
              .map(serializeVisualization)
              .getOrElse(null),
            medidasParaPersonalizar,
          },
        });
    },

    excluirArquivoMedidas: arquivo => {
      return api.unbatchedClient
        .post("/configuracaorelatorio/excluirArquivo")
        .send(arquivo);
    },

    obterFuncaoQtlink: (
      ativosSelecionados,
      medidasSelecionadas,
      nomeGrupoAtivosSelecionado,
      configuracaoGlobalRelatorio
    ) => {
      return api.unbatchedClient
        .post(joinPath("/configuracaorelatorio", modoApp, "obterFuncaoQtlink"))
        .send({
          ativos: ativosSelecionados,
          medidas: medidasSelecionadas,
          configuracaoGlobalRelatorio: {
            ...configuracaoGlobalRelatorio,
            nomeGrupoAtivosAberto: nomeGrupoAtivosSelecionado,
          },
        });
    },

    validarSintaxe: (expressao, medidasSelecionadas) =>
      api.unbatchedClient
        .post(
          joinPath(
            "/configuracaorelatorio",
            modoApp,
            "validarExpressaoMedidaComposta"
          )
        )
        .send({
          expressao,
          medidasSelecionadas,
        }),

    buscaAtivos: query =>
      api.unbatchedClient.get(
        `/configuracaorelatorio/buscarAtivoCarteira?campoBusca=${query}`
      ),

    buscaAtivoSelecionavel: (query, filtroBusca) =>
      api.unbatchedClient.get(
        `/configuracaorelatorio/buscarAtivo?${buildQueryString({
          searchString: query,
          filtroBusca,
        })}`
      ),

    buscaAtivosColecao: (identificador, tipoItemSelecionavel) =>
      api.unbatchedClient.get(
        `/configuracaorelatorio/obterAtivosColecao?${buildQueryString({
          identificador,
          tipoItemSelecionavel,
        })}`
      ),

    post,

    buscaInformacoesAdicionaisAtivos: ativos =>
      post("/ativos/informacoesAdicionais").send(ativos),

    currentUser: () =>
      get("/usuarios/detalhes").then(({ email, login, nome }) => ({
        email,
        login,
        name: nome,
      })),

    reportConfiguration: ({ id }) => get(`/relatorio/${id}`),

    reportResultConfiguration: ({ id }) =>
      get(`/acessoDados/v2/relatorio/${id}/configuracao`).then(result => ({
        ...result,
        tipoRelatorio: modoApp,
      })),

    reportVisualization: ({ id }) =>
      get(`/acessoDados/v2/relatorio/${id}/visualizacao`).then(
        parseVisualization
      ),

    saveReportVisualization: ({ id, visualization }) =>
      put(`/acessoDados/v2/relatorio/${id}/visualizacao`).send(
        serializeVisualization(visualization)
      ),

    reportExcel: ({ id }) =>
      baseGet(
        joinPath("/acessoDados", "v2", "relatorio", modoApp, id, "excel")
      ),

    reportCSV: ({ id }) =>
      baseGet(joinPath("/acessoDados", "v2", "relatorio", modoApp, id, "csv")),

    generateReport: async (report, visualization) =>
      post("/acessoDados/v2/relatorio").send({
        medida: modoApp === MODO_APP_SERIES ? "SERIES_NOVO" : "RELATORIO",
        modoRelatorio: modoApp,
        selecao: report.itemSelecionavel,
        configuracao: report.configuracaoRelatorio,
        exibirAtivos: true,
        exibirValoresFundosEuropa: true,
        ...(modoApp === MODO_APP_SERIES
          ? {
              aplicarconfiguracoesadicionaisresultado: false,
              removeridentificadoresativosnaoselecionados: false,
            }
          : {}),
        ...(modoApp === MODO_APP_SERIES && !!visualization
          ? { visualizacao: serializeVisualization(visualization) }
          : {}),
      }),

    reportRows: async ({ id }) => get(`/acessoDados/v2/relatorio/${id}`),

    reportExists: async ({ id }) =>
      api.baseClient
        .head(`/acessoDados/v2/relatorio/${id}`)
        .then(res => res.ok)
        .catch(() => false),

    removeReportAsset: ({ id, assetId, assetType }) =>
      api.client
        .delete(`/acessoDados/v2/relatorio/${id}/ativo/${assetType}`)
        .send(assetId)
        .catch(_ => null),

    cloneReport: ({ id }) => put(`/acessoDados/v2/relatorio/${id}/clone`),

    mergeReports: ({ sourceId, destinationId }) =>
      put(`/acessoDados/v2/relatorio/${destinationId}/merge/${sourceId}`),

    saveAssetGroup: ({ assets, name, override }) =>
      post(
        `/ativos/grupo/salvar?nome=${encodeURIComponent(
          name
        )}&sobrescrever=${override}`
      ).send(
        assets.map(({ id, type }) => ({
          identificador: id,
          tipoItemSelecionavel: type,
        }))
      ),

    atualizarMetricaGrafico: ({ id }) =>
      put(`/acessoDados/v2/relatorio/${id}/metrica/grafico`),
  };
};
